import { Component } from '@theme/component';

/**
 * Product comparison with per-card color variant previews.
 *
 * @extends {Component}
 */
class ProductComparison extends Component {
  connectedCallback() {
    super.connectedCallback();

    for (const card of this.querySelectorAll('[data-comparison-card]')) {
      this.#showInitialImage(card);
    }
  }

  /**
   * Select a color variant and reveal its product image.
   *
   * @param {PointerEvent} event
   */
  selectVariant(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-variant-trigger]') : null;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const card = trigger.closest('[data-comparison-card]');
    if (!(card instanceof HTMLElement)) return;

    const variantIndex = trigger.dataset.variantIndex;
    if (!variantIndex) return;

    this.#setPressedSwatch(card, trigger);
    this.#showImage(card, variantIndex);
  }

  /**
   * Show the previous configured product image.
   *
   * @param {PointerEvent} event
   */
  showPreviousImage(event) {
    this.#showAdjacentImage(event, -1);
  }

  /**
   * Show the next configured product image.
   *
   * @param {PointerEvent} event
   */
  showNextImage(event) {
    this.#showAdjacentImage(event, 1);
  }

  /**
   * @param {Element} card
   */
  #showInitialImage(card) {
    const pressedSwatch = card.querySelector('[data-variant-trigger][aria-pressed="true"]');
    const defaultImageIndex = card.getAttribute('data-default-image-index') || 'default';
    const variantIndex =
      pressedSwatch instanceof HTMLButtonElement ? pressedSwatch.dataset.variantIndex || defaultImageIndex : defaultImageIndex;

    this.#showImage(card, variantIndex);
  }

  /**
   * @param {Element} card
   * @param {HTMLButtonElement} selectedTrigger
   */
  #setPressedSwatch(card, selectedTrigger) {
    for (const trigger of card.querySelectorAll('[data-variant-trigger]')) {
      trigger.setAttribute('aria-pressed', String(trigger === selectedTrigger));
    }
  }

  /**
   * @param {PointerEvent} event
   * @param {number} step
   */
  #showAdjacentImage(event, step) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-variant-direction]') : null;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const card = trigger.closest('[data-comparison-card]');
    if (!(card instanceof HTMLElement)) return;

    const images = Array.from(card.querySelectorAll('[data-variant-image]'));
    if (images.length < 2) return;

    const currentIndex = images.findIndex((image) => image.classList.contains('is-active'));
    const nextIndex = (Math.max(currentIndex, 0) + step + images.length) % images.length;
    const variantIndex = images[nextIndex]?.getAttribute('data-variant-index');
    if (!variantIndex) return;

    const swatch = card.querySelector(`[data-variant-trigger][data-variant-index="${variantIndex}"]`);
    if (swatch instanceof HTMLButtonElement) this.#setPressedSwatch(card, swatch);
    this.#showImage(card, variantIndex);
  }

  /**
   * @param {Element} card
   * @param {string} variantIndex
   */
  #showImage(card, variantIndex) {
    const images = Array.from(card.querySelectorAll('[data-variant-image]'));
    if (images.length === 0) return;

    const selectedImage = images.find((image) => image.getAttribute('data-variant-index') === variantIndex);
    const defaultImageIndex = card.getAttribute('data-default-image-index') || 'default';
    const availableImage =
      images.find((image) => image.getAttribute('data-variant-index') === defaultImageIndex) ||
      images[0];
    const activeImage = selectedImage || availableImage;

    for (const image of images) {
      const isActive = image === activeImage;
      image.classList.toggle('is-active', isActive);
      image.setAttribute('aria-hidden', String(!isActive));
    }
  }
}

if (!customElements.get('product-comparison')) {
  customElements.define('product-comparison', ProductComparison);
}
